import { ENTITY_TYPE } from '../../../../../common/MagicUpload/Utils/MagicUploadConstants';
import { GlobalSearchResult } from '../../../../../common/GlobalSearch/useSearchResultFetcher';
import { ADD_AS_INDIVIDUAL_PAN, AddAsIndividualPan } from '../../../../../store/investor/service';

/**
 * Pure helpers for resolving the identifier sent when adding a lead.
 *
 * Kept out of the component so the precedence logic is unit-testable without
 * rendering. Used by AddToWatchlistV2.
 */

/**
 * Picks the most precise identifier for a search result: CIN, then PAN, then a
 * caller-supplied fallback (typically the org name).
 *
 * Note on GSTIN: a GSTIN typed in the search bar is converted to its PAN
 * server-side (globalSearchV2), so a GSTIN-originated result arrives here with
 * `pan` populated — no special-casing needed.
 */
export const extractIdentifier = (result: any, fallback?: string): string => result?.cinNumber || result?.pan || fallback || '';

export interface ResolveAddLeadIdentifierArgs {
    /** True only for a genuine "add by typed text" flow (no real entity picked). */
    isAddUsingFlow: boolean;
    /** The name currently shown in the identifier input. */
    displayName?: string;
    /** Identifier captured when a real entity was picked (CIN/PAN). */
    payloadIdentifier?: string;
    /** Legal name resolved from prefill, if any. */
    legalNameFromPrefill?: string;
}

/**
 * Resolves the `identifier` value sent to addCompanyToWatchlist.
 *
 * - Genuine add-by-typed-text (`isAddUsingFlow`) → the typed display name.
 * - Otherwise (a real entity was picked, which resets the flag) → the captured
 *   payload identifier (CIN/PAN), then the prefill legal name, then the display
 *   name as a last resort.
 *
 * Returns '' when nothing resolves; callers omit the field on a falsy value.
 */
export const resolveAddLeadIdentifier = ({
    isAddUsingFlow,
    displayName,
    payloadIdentifier,
    legalNameFromPrefill,
}: ResolveAddLeadIdentifierArgs): string => (isAddUsingFlow ? displayName : payloadIdentifier || legalNameFromPrefill || displayName) || '';

export type EntityCardCategory = 'SOLE_PROPRIETOR' | 'INDIVIDUAL' | 'COMPANY' | null;

/**
 * Single source of truth for the "which kind of card is this" precedence rule,
 * shared by resolveEntityPickIntent (request payload) and AddNewPill (chip
 * label): an entity-search `entityType` always wins over the legacy
 * `isIndividualPan` flag.
 *
 * - `entityType === 'PERSON'`  → 'SOLE_PROPRIETOR' (sole-prop entity row)
 * - `entityType === 'COMPANY'` → 'COMPANY'
 * - `isIndividualPan === true` → 'INDIVIDUAL' (legacy pan-profile card)
 * - otherwise                  → null (legacy company card / no selection)
 */
export const classifyEntityCard = (result?: GlobalSearchResult | null): EntityCardCategory => {
    if (result?.entityType === ENTITY_TYPE.PERSON) return 'SOLE_PROPRIETOR';
    if (result?.entityType === ENTITY_TYPE.COMPANY) return 'COMPANY';
    if (result?.isIndividualPan === true) return 'INDIVIDUAL';
    return null;
};

export interface EntityPickIntent {
    addAsIndividualPan?: AddAsIndividualPan;
    selectedEntityId?: string;
}

/**
 * Resolves the intent to send BE for the picked global-search card, so it can
 * route between the sole-proprietor entity, the company entity, or the legacy
 * pan-profile individual branch:
 *
 * - 'SOLE_PROPRIETOR' → PROPRIETOR + selectedEntityId (sole-prop entity row)
 * - 'COMPANY'         → COMPANY + selectedEntityId (fixes multi-company ordering)
 * - 'INDIVIDUAL'      → INDIVIDUAL, no id (legacy pan-profile card)
 * - null              → {} (BE auto-decision, legacy behaviour)
 *
 * Note: an entity-search card (`entityType` PERSON/COMPANY) with a missing
 * `entityId` (older client / partial card) still sends the matching
 * addAsIndividualPan with no selectedEntityId — this is intentional. BE's
 * type-fallback for that exact case picks the matching row (or fails loud);
 * silently falling back to legacy (omitting addAsIndividualPan) here would
 * recreate the original ambiguous-pick bug this ticket fixes.
 */
export const resolveEntityPickIntent = (selectedAddNewResult?: GlobalSearchResult | null): EntityPickIntent => {
    const category = classifyEntityCard(selectedAddNewResult);
    const entityId = selectedAddNewResult?.entityId;

    if (category === 'SOLE_PROPRIETOR') {
        // selectedEntityId (the DIN) is the row selector, scoped to the sole-prop card only —
        // it disambiguates the person row from the company row when both share a person PAN.
        return { addAsIndividualPan: ADD_AS_INDIVIDUAL_PAN.PROPRIETOR, ...(entityId && { selectedEntityId: entityId }) };
    }
    if (category === 'COMPANY') {
        // The company card carries a CIN, which is already unambiguous on the BE, so it stays on
        // the unchanged CIN path (incl. multi-PAN resolution) — do NOT send selectedEntityId here.
        return { addAsIndividualPan: ADD_AS_INDIVIDUAL_PAN.COMPANY };
    }
    if (category === 'INDIVIDUAL') {
        return { addAsIndividualPan: ADD_AS_INDIVIDUAL_PAN.INDIVIDUAL };
    }
    return {};
};

export interface AddCompanyToWatchlistResponse {
    identifierErrorMessage?: string;
    orgInfo?: { assesseeOrgId?: string };
}

/**
 * Resolves the identifier-field error to surface after an addCompanyToWatchlist
 * response: BE's `identifierErrorMessage` (e.g. a pick that couldn't be honored)
 * takes precedence over the generic "couldn't be identified" fallback, which
 * only fires when no org was created.
 */
export const resolveAddLeadIdentifierError = (data: AddCompanyToWatchlistResponse | undefined, fallbackMessage: string): string | null => {
    if (data?.identifierErrorMessage) return data.identifierErrorMessage;
    if (!data?.orgInfo?.assesseeOrgId) return fallbackMessage;
    return null;
};
